// AUD-3 — note-block instrument voices. The 16 Minecraft note-block instruments as
// synth patches, keyed by the instrument name that `sim/game/note_block.instrumentFor`
// picks from the block below. Each patch voices the instrument at note 12 (its
// pitch=1.0 reference); the host plays it with `pitch = 2^((note−12)/12)` so the
// two-octave note range (0..24) falls out of the playback multiplier. PURE — no
// WebAudio/DOM; the boot host feeds these into `AudioEngine.playPatch`.
import type { Patch } from './patch';

/** The 16 note-block instruments (Java's set). */
export const NOTE_INSTRUMENTS = [
  'harp', 'bass', 'basedrum', 'snare', 'hat', 'bell', 'flute', 'chime',
  'guitar', 'xylophone', 'iron_xylophone', 'cow_bell', 'didgeridoo', 'bit', 'banjo', 'pling',
] as const;
export type NoteInstrument = (typeof NOTE_INSTRUMENTS)[number];

// A short plucked envelope (most melodic instruments) and a longer rung one.
const pluck = (decayMs: number, releaseMs: number) =>
  ({ attackMs: 1, decayMs, sustain: 0.0, releaseMs } as const);
const ring = (decayMs: number, releaseMs: number, sustain = 0.15) =>
  ({ attackMs: 2, decayMs, sustain, releaseMs } as const);

/** Instrument → patch (voiced at the note-12 reference pitch). */
const INSTRUMENTS: Record<NoteInstrument, Patch> = {
  harp: { wave: 'triangle', freq: 392, durationMs: 500, env: pluck(220, 260), gain: 0.34 },
  bass: { wave: 'triangle', freq: 98, durationMs: 520, env: pluck(260, 240), gain: 0.4,
    filter: { mode: 'lowpass', startHz: 700, endHz: 250, q: 0.8 } },
  basedrum: { wave: 'sine', freq: 80, durationMs: 220, env: pluck(90, 120), gain: 0.5,
    pitchGlide: { from: 2.4, to: 1.0, glideMs: 70 } },
  snare: { wave: 'noise', freq: 1, durationMs: 200, env: pluck(60, 130), gain: 0.4,
    filter: { mode: 'bandpass', startHz: 2000, endHz: 1400, q: 1.0 } },
  hat: { wave: 'noise', freq: 1, durationMs: 110, env: pluck(20, 70), gain: 0.3,
    filter: { mode: 'highpass', startHz: 8000, endHz: 7000, q: 0.9 } },
  bell: { wave: 'sine', freq: 660, durationMs: 900, env: ring(400, 460), gain: 0.32, detuneCents: 1200 },
  flute: { wave: 'sine', freq: 523, durationMs: 600, env: ring(120, 300, 0.5), gain: 0.3,
    vibrato: { rateHz: 5, depth: 0.01 } },
  chime: { wave: 'sine', freq: 1046, durationMs: 700, env: ring(320, 360), gain: 0.3, detuneCents: 1902 },
  guitar: { wave: 'saw', freq: 330, durationMs: 460, env: pluck(200, 240), gain: 0.32,
    filter: { mode: 'lowpass', startHz: 2600, endHz: 700, q: 0.9 } },
  xylophone: { wave: 'triangle', freq: 880, durationMs: 360, env: pluck(150, 190), gain: 0.32 },
  iron_xylophone: { wave: 'triangle', freq: 784, durationMs: 500, env: ring(240, 240), gain: 0.32,
    filter: { mode: 'bandpass', startHz: 2400, endHz: 1600, q: 1.3 } },
  cow_bell: { wave: 'square', freq: 560, durationMs: 260, env: pluck(120, 130), gain: 0.28,
    filter: { mode: 'bandpass', startHz: 1800, endHz: 1500, q: 1.4 } },
  didgeridoo: { wave: 'saw', freq: 73, durationMs: 700, env: ring(200, 260, 0.4), gain: 0.38,
    filter: { mode: 'lowpass', startHz: 700, endHz: 300, q: 0.7 }, vibrato: { rateHz: 6, depth: 0.02 } },
  bit: { wave: 'square', freq: 440, durationMs: 360, env: pluck(160, 190), gain: 0.28 },
  banjo: { wave: 'saw', freq: 494, durationMs: 320, env: pluck(120, 190), gain: 0.3,
    filter: { mode: 'lowpass', startHz: 3200, endHz: 1200, q: 0.9 } },
  pling: { wave: 'sine', freq: 440, durationMs: 420, env: pluck(190, 220), gain: 0.32, detuneCents: 5 },
};

/** The patch for an instrument (falls back to harp for an unknown name). */
export function noteVoice(instrument: string): Patch {
  return INSTRUMENTS[instrument as NoteInstrument] ?? INSTRUMENTS.harp;
}
